import * as React from 'react';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    List,
    ListItem,
    MenuItem,
    Select,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {alpha} from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import {useTranslation} from 'react-i18next';
import {getItemTreatmentColors, getItemTreatmentLabelKey, MonthlyChangeItem} from '../../../../hooks/useMonthlyChange';

interface MonthlyChangeItemsDialogProps {
    open: boolean;
    onClose: () => void;
    periodLabel: string;
    items: MonthlyChangeItem[];
    itemsSearchQuery: string;
    setItemsSearchQuery: (value: string) => void;
    itemsTypeFilter: 'all' | 'Allowance' | 'Deduction';
    setItemsTypeFilter: (value: 'all' | 'Allowance' | 'Deduction') => void;
    itemsBasisFilter: 'all' | 'NET' | 'GROSS';
    setItemsBasisFilter: (value: 'all' | 'NET' | 'GROSS') => void;
    addItemFormOpen: boolean;
    setAddItemFormOpen: (open: boolean) => void;
    description: string;
    setDescription: (value: string) => void;
    type: string;
    setType: (value: string) => void;
    calcType: string;
    setCalcType: (value: string) => void;
    handleAddMonthlyChange: () => Promise<void>;
    isFormDisabled: boolean;
    setDeleteItemId: (id: string) => void;
    setDeleteConfirmOpen: (open: boolean) => void;
}

export default function MonthlyChangeItemsDialog({
    open,
    onClose,
    periodLabel,
    items,
    itemsSearchQuery,
    setItemsSearchQuery,
    itemsTypeFilter,
    setItemsTypeFilter,
    itemsBasisFilter,
    setItemsBasisFilter,
    addItemFormOpen,
    setAddItemFormOpen,
    description,
    setDescription,
    type,
    setType,
    calcType,
    setCalcType,
    handleAddMonthlyChange,
    isFormDisabled,
    setDeleteItemId,
    setDeleteConfirmOpen,
}: Readonly<MonthlyChangeItemsDialogProps>): React.ReactElement {
    const {t} = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const tabValue = itemsTypeFilter;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" fullScreen={isMobile}>
            <DialogTitle sx={{pb: 1}}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                    <Box sx={{textAlign: 'left'}}>
                        <Typography variant="h5" fontWeight={700}>{t('payroll.monthlyChange.dialog.title')}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t('payroll.monthlyChange.dialog.subtitle', {period: periodLabel})}
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                        {!isMobile && (
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={<AddIcon/>}
                                onClick={() => setAddItemFormOpen(!addItemFormOpen)}
                                disabled={isFormDisabled}
                            >
                                {t('payroll.monthlyChange.dialog.addItemButton')}
                            </Button>
                        )}
                        <IconButton onClick={onClose} aria-label="close">
                            <CloseIcon/>
                        </IconButton>
                    </Stack>
                </Stack>
            </DialogTitle>
            <DialogContent dividers sx={{p: {xs: 2, sm: 3}}}>
                {addItemFormOpen && (
                    <Box sx={{
                        mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1,
                        backgroundColor: 'action.hover',
                    }}>
                        <Typography variant="subtitle2" fontWeight={700} sx={{mb: 1.5, textAlign: 'left'}}>
                            {t('payroll.monthlyChange.dialog.newItemTitle')}
                        </Typography>
                        <Stack direction={{xs: 'column', sm: 'row'}} spacing={1.5}>
                            <FormControl fullWidth size="small" disabled={isFormDisabled}>
                                <InputLabel id="items-dialog-type-label">{t('payroll.monthlyChange.chooseType')}</InputLabel>
                                <Select
                                    labelId="items-dialog-type-label"
                                    label={t('payroll.monthlyChange.chooseType')}
                                    value={type}
                                    onChange={(e) => setType(e.target.value)}
                                >
                                    <MenuItem value="Allowance">{t('payroll.monthlyChange.type.allowance')}</MenuItem>
                                    <MenuItem value="Deduction">{t('payroll.monthlyChange.type.deduction')}</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl fullWidth size="small" disabled={isFormDisabled}>
                                <InputLabel id="items-dialog-basis-label">{t('payroll.run.table.header.calcType')}</InputLabel>
                                <Select
                                    labelId="items-dialog-basis-label"
                                    label={t('payroll.run.table.header.calcType')}
                                    value={calcType}
                                    onChange={(e) => setCalcType(e.target.value)}
                                >
                                    <MenuItem value="NET">NET</MenuItem>
                                    <MenuItem value="GROSS">GROSS</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                fullWidth
                                size="small"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                label={t('payroll.monthlyChange.description')}
                                placeholder={t('payroll.monthlyChange.descriptionPlaceholder')}
                                disabled={isFormDisabled}
                            />
                        </Stack>
                        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{mt: 1.5}}>
                            <Button onClick={() => setAddItemFormOpen(false)}>{t('common.actions.cancel')}</Button>
                            <Button
                                variant="contained"
                                disabled={isFormDisabled || !description.trim() || !type || !calcType}
                                onClick={async () => {
                                    await handleAddMonthlyChange();
                                    setAddItemFormOpen(false);
                                }}
                            >
                                {t('payroll.monthlyChange.addButton')}
                            </Button>
                        </Stack>
                    </Box>
                )}

                <Stack direction={{xs: 'column', sm: 'row'}} spacing={1.5} sx={{mb: 2}}>
                    <TextField
                        fullWidth
                        size="small"
                        value={itemsSearchQuery}
                        onChange={(e) => setItemsSearchQuery(e.target.value)}
                        placeholder={t('payroll.monthlyChange.dialog.searchPlaceholder')}
                        InputProps={{startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small"/></InputAdornment>}}
                    />
                    <FormControl size="small" sx={{minWidth: {xs: '100%', sm: 180}}}>
                        <InputLabel id="items-dialog-basis-filter-label">{t('payroll.monthlyChange.dialog.basisLabel')}</InputLabel>
                        <Select
                            labelId="items-dialog-basis-filter-label"
                            label={t('payroll.monthlyChange.dialog.basisLabel')}
                            value={itemsBasisFilter}
                            onChange={(e) => setItemsBasisFilter(e.target.value as 'all' | 'NET' | 'GROSS')}
                        >
                            <MenuItem value="all">{t('payroll.monthlyChange.dialog.basisAll')}</MenuItem>
                            <MenuItem value="NET">NET</MenuItem>
                            <MenuItem value="GROSS">GROSS</MenuItem>
                        </Select>
                    </FormControl>
                </Stack>

                <Tabs
                    value={tabValue}
                    onChange={(_e, value) => setItemsTypeFilter(value)}
                    sx={{mb: 1, borderBottom: '1px solid', borderColor: 'divider'}}
                >
                    <Tab value="all" label={t('payroll.monthlyChange.dialog.tabAll')}/>
                    <Tab value="Allowance" label={t('payroll.monthlyChange.dialog.tabAllowance')}/>
                    <Tab value="Deduction" label={t('payroll.monthlyChange.dialog.tabDeduction')}/>
                </Tabs>

                {!isMobile && items.length > 0 && (
                    <Stack direction="row" sx={{px: 1, py: 1, color: 'text.secondary'}}>
                        <Box sx={{flex: 2, textAlign: 'left'}}><Typography variant="caption" fontWeight={700}>{t('payroll.monthlyChange.dialog.header.item')}</Typography></Box>
                        <Box sx={{flex: 1, textAlign: 'left'}}><Typography variant="caption" fontWeight={700}>{t('payroll.monthlyChange.dialog.header.calculation')}</Typography></Box>
                        <Box sx={{flex: 1, textAlign: 'left'}}><Typography variant="caption" fontWeight={700}>{t('payroll.monthlyChange.dialog.header.status')}</Typography></Box>
                        <Box sx={{width: 40}}/>
                    </Stack>
                )}

                {items.length === 0 ? (
                    <Box sx={{textAlign: 'center', py: 6}}>
                        <SwapHorizIcon sx={{fontSize: 48, color: 'text.disabled', mb: 1}}/>
                        <Typography variant="body1" color="text.secondary">{t('payroll.monthlyChange.empty.title')}</Typography>
                        <Typography variant="body2" color="text.disabled">{t('payroll.monthlyChange.empty.description')}</Typography>
                    </Box>
                ) : (
                    <List disablePadding>
                        {items.map((item) => {
                            const colors = getItemTreatmentColors(item);
                            return (
                                <React.Fragment key={item.id}>
                                    <ListItem sx={{px: 1, py: 1.5}}>
                                        <Stack direction="row" alignItems="center" sx={{width: '100%', flexWrap: 'wrap', gap: 1}}>
                                            <Box sx={{flex: {xs: '1 1 100%', sm: 2}, textAlign: 'left'}}>
                                                <Typography variant="body2" fontWeight={700}>{item.description}</Typography>
                                                <Chip
                                                    label={t(getItemTreatmentLabelKey(item))}
                                                    size="small"
                                                    sx={{mt: 0.5, height: 20, fontSize: '0.7rem', backgroundColor: colors.bg, color: colors.color}}
                                                />
                                            </Box>
                                            <Box sx={{flex: 1, textAlign: 'left'}}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {t('payroll.monthlyChange.dialog.calculationFixedAmount')}
                                                </Typography>
                                            </Box>
                                            <Box sx={{flex: 1, textAlign: 'left'}}>
                                                <Chip
                                                    label={t('payroll.monthlyChange.dialog.statusActive')}
                                                    size="small"
                                                    color="success"
                                                    variant="outlined"
                                                />
                                            </Box>
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    setDeleteItemId(item.id);
                                                    setDeleteConfirmOpen(true);
                                                }}
                                                disabled={isFormDisabled}
                                                title={t('common.actions.delete')}
                                            >
                                                <DeleteOutlineIcon fontSize="small" color="error"/>
                                            </IconButton>
                                        </Stack>
                                    </ListItem>
                                    <Divider/>
                                </React.Fragment>
                            );
                        })}
                    </List>
                )}

                <Stack direction="row" spacing={1} alignItems="flex-start" sx={{
                    mt: 2, p: 1.5, borderRadius: 1,
                    backgroundColor: (theme) => alpha(theme.palette.info.main, 0.08),
                    border: '1px solid', borderColor: 'info.light',
                }}>
                    <InfoOutlinedIcon fontSize="small" color="info"/>
                    <Typography variant="caption" color="text.secondary" sx={{textAlign: 'left'}}>
                        {t('payroll.monthlyChange.dialog.hint')}
                    </Typography>
                </Stack>

                {isMobile && (
                    <Button
                        fullWidth
                        variant="contained"
                        color="secondary"
                        startIcon={<AddIcon/>}
                        onClick={() => setAddItemFormOpen(!addItemFormOpen)}
                        disabled={isFormDisabled}
                        sx={{mt: 2}}
                    >
                        {t('payroll.monthlyChange.dialog.addItemButton')}
                    </Button>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant={isMobile ? 'outlined' : 'text'} fullWidth={isMobile}>
                    {t('common.actions.close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
